import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faTrash, faPlus, faSave, faCheck, faTimes, faEye, faLayerGroup, faCopy } from "@fortawesome/free-solid-svg-icons";
import { productService } from "../services/product";
import { authService } from "../services/auth";
import { productParametersService, getParameterTypeLabel } from "../services/productParameters";
import ParameterTemplateEditor from "../components/ParameterTemplateEditor";
import BoxTypeSelect from "../components/BoxTypeSelect";
import { flattenFields } from "../utils/parameterTree";
import toast from "react-hot-toast";

const EditProducts = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get product ID from URL
  const [boxTypeOptions, setBoxTypeOptions] = useState([]);
  const [productData, setProductData] = useState({
    productCode: "",
    name: "",
    description: "",
    // category removido conforme solicitação
    isCured: false,
    curingTimeDays: 0,
    initialWeightKg: 0,
    curedWeightKg: 0,
    minStockQuantity: 0,
    maxStockQuantity: 0,
    advancedDetails: "{}", // JSON string
    imageLink: "", // Campo para URL da imagem
    boxTypeDpd: "", // Tipo de caixa para clientes DPD
    boxTypeOx: "", // Tipo de caixa para clientes OX
    validadeDias: 365, // Campo para validade em dias
    isPack: false, // Novo campo: indica se é um pack
    unitsPerPack: 1, // Novo campo: quantidade de unidades por pack
    isActive: true, // Novo campo: produto ativo
    produtividade: 20, // Novo campo: produtividade em % (padrão 20%)
    isVariant: false, // Novo campo: indica se é variante
    parentProductId: "", // Novo campo: ID do produto pai
    forAllClients: true, // Novo campo: produto para todos os clientes
    soldBy: "unit", // unit = por unidade, kg = por peso
    hideFromOrdersWhenOutOfStock: false, // Novo campo: ocultar das encomendas se stock zerado
  });
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  const [loading, setLoading] = useState(true); // Set to true initially to load data
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentUser, setCurrentUser] = useState(null); // Estado para o usuário logado
  const [showPasswordModal, setShowPasswordModal] = useState(false); // Novo estado para o modal de senha
  const [deletePassword, setDeletePassword] = useState(""); // Novo estado para a senha de exclusão
  const [randomCode, setRandomCode] = useState(""); // Novo estado para o código aleatório
  const [enteredCode, setEnteredCode] = useState(""); // Novo estado para o código digitado pelo usuário
  const [availableParents, setAvailableParents] = useState([]); // Lista de produtos disponíveis como pais

  // Estados para parâmetros de produção
  const [parameterTemplates, setParameterTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newParameters, setNewParameters] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [viewingFields, setViewingFields] = useState(null); // ID do template cujos campos estão sendo visualizados

  // Estados para transferência de stock entre produtos
  const [enableStockTransfer, setEnableStockTransfer] = useState(false);
  const [transferLinkedProductIds, setTransferLinkedProductIds] = useState([]);
  const [allProductsForTransfer, setAllProductsForTransfer] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchAvailableParents = async () => {
      try {
        const parents = await productService.getAvailableParents();
        setAvailableParents(parents);
      } catch (error) {
        console.error("Erro ao buscar produtos disponíveis:", error);
      }
    };
    fetchAvailableParents();
  }, []);

  // Buscar produtos compatíveis já configurados e lista geral de produtos para transferência de stock
  useEffect(() => {
    const fetchTransferData = async () => {
      if (!id) return;
      try {
        const [links, products] = await Promise.all([
          productService.getTransferLinks(id),
          productService.getProducts(),
        ]);
        const linkedIds = Array.isArray(links) ? links.map((p) => p.id) : [];
        setTransferLinkedProductIds(linkedIds);
        setEnableStockTransfer(linkedIds.length > 0);
        setAllProductsForTransfer(
          Array.isArray(products) ? products.filter((p) => !p.is_variant && p.id !== id) : []
        );
      } catch (error) {
        console.error("Erro ao buscar dados de transferência de stock:", error);
      }
    };
    fetchTransferData();
  }, [id]);

  useEffect(() => {
    productService
      .getBoxTypes()
      .then(setBoxTypeOptions)
      .catch(() => setBoxTypeOptions([]));
  }, []);

  const handleBoxTypeDpdChange = (value) => {
    setProductData((prevData) => ({ ...prevData, boxTypeDpd: value }));
  };

  const handleBoxTypeOxChange = (value) => {
    setProductData((prevData) => ({ ...prevData, boxTypeOx: value }));
  };

  // app_metadata é a fonte real de autorização (só o service-role escreve;
  // user_metadata é auto-editável pelo utilizador — #109). Fallback
  // transitório para user_metadata cobre sessões abertas antes da migração
  // de metadata; o servidor sempre aplica a regra via app_metadata mesmo
  // que a UI aqui mostre um botão indevido. Remover o fallback após a
  // migração de todos os admins/supervisores estar validada em produção.
  const currentUserRole =
    currentUser?.app_metadata?.role || currentUser?.user_metadata?.role;

  // Verifica se o usuário logado é admin ou supervisor
  const isAdminOrSupervisor =
    currentUserRole === "admin" || currentUserRole === "supervisor";

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const product = await productService.getProductById(id);
        setProductData({
          productCode: product.product_code || "",
          name: product.name || "",
          description: product.description || "",
          isCured: product.is_cured || false,
          curingTimeDays: product.curing_time_days || 0,
          initialWeightKg: product.initial_weight_kg || 0,
          curedWeightKg: product.cured_weight_kg || 0,
          minStockQuantity: product.min_stock_quantity || 0,
          maxStockQuantity: product.max_stock_quantity || 0,
          advancedDetails: product.advanced_details
            ? JSON.stringify(product.advanced_details, null, 2)
            : "{}",
          imageLink: product.image_url || "", // Carrega URL da imagem
          boxTypeDpd: product.box_type_dpd || product.box_type || "", // Tipo de caixa DPD
          boxTypeOx: product.box_type_ox || product.box_type || "", // Tipo de caixa OX
          validadeDias: product.validade_dias || 365,
          isPack: product.is_pack || false, // Novo campo
          unitsPerPack: product.units_per_pack || 1, // Novo campo
          isActive: product.is_active !== undefined ? product.is_active : true, // Novo campo
          produtividade: product.produtividade || 20, // Novo campo
          isVariant: product.is_variant || false, // Novo campo
          parentProductId: product.parent_product_id || "", // Novo campo
          forAllClients: product.for_all_clients !== undefined ? product.for_all_clients : true, // Novo campo
          soldBy: product.sold_by || "unit",
          hideFromOrdersWhenOutOfStock: product.hide_from_orders_when_out_of_stock !== undefined ? product.hide_from_orders_when_out_of_stock : false, // Novo campo
        });
        setShowAdvancedDetails(!!product.advanced_details); // Show if advanced details exist
        setError("");
      } catch (err) {
        setError(err.message || "Erro ao carregar produto para edição.");
        toast.error(err.message || "Erro ao carregar produto para edição.");
        console.error("Erro ao carregar produto:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Buscar templates de parâmetros do produto
  useEffect(() => {
    const fetchParameterTemplates = async () => {
      if (!id) return;
      try {
        setLoadingParameters(true);
        const templates = await productParametersService.getTemplates(id);
        setParameterTemplates(templates);
        const active = templates.find((t) => t.is_active);
        setActiveTemplate(active || null);
      } catch (err) {
        console.error("Erro ao buscar templates de parâmetros:", err);
      } finally {
        setLoadingParameters(false);
      }
    };
    fetchParameterTemplates();
  }, [id]);

  // Criar novo template de parâmetros
  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Nome do template é obrigatório");
      return;
    }
    if (newParameters.length === 0) {
      toast.error("Adicione pelo menos um parâmetro");
      return;
    }
    const invalidParams = newParameters.filter((p) => !p.name?.trim());
    if (invalidParams.length > 0) {
      toast.error("Todos os parâmetros devem ter um nome");
      return;
    }

    try {
      setLoadingParameters(true);
      await productParametersService.createTemplate({
        product_id: id,
        template_name: newTemplateName,
        parameters: newParameters,
      });
      toast.success("Template criado com sucesso!");
      setIsCreatingTemplate(false);
      setNewTemplateName("");
      setNewParameters([]);
      // Recarregar templates
      const templates = await productParametersService.getTemplates(id);
      setParameterTemplates(templates);
      const active = templates.find((t) => t.is_active);
      setActiveTemplate(active || null);
    } catch (err) {
      toast.error(err.message || "Erro ao criar template");
    } finally {
      setLoadingParameters(false);
    }
  };

  // Ativar template
  const handleActivateTemplate = async (templateId) => {
    try {
      setLoadingParameters(true);
      await productParametersService.activateTemplate(templateId);
      toast.success("Template ativado!");
      const templates = await productParametersService.getTemplates(id);
      setParameterTemplates(templates);
      const active = templates.find((t) => t.is_active);
      setActiveTemplate(active || null);
    } catch (err) {
      toast.error(err.message || "Erro ao ativar template");
    } finally {
      setLoadingParameters(false);
    }
  };

  // Desativar template
  const handleDeactivateTemplate = async (templateId) => {
    try {
      setLoadingParameters(true);
      await productParametersService.deactivateTemplate(templateId);
      toast.success("Template desativado!");
      const templates = await productParametersService.getTemplates(id);
      setParameterTemplates(templates);
      setActiveTemplate(null);
    } catch (err) {
      toast.error(err.message || "Erro ao desativar template");
    } finally {
      setLoadingParameters(false);
    }
  };

  // Deletar template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Tem certeza que deseja deletar este template?")) return;
    try {
      setLoadingParameters(true);
      await productParametersService.deleteTemplate(templateId);
      toast.success("Template deletado!");
      const templates = await productParametersService.getTemplates(id);
      setParameterTemplates(templates);
      const active = templates.find((t) => t.is_active);
      setActiveTemplate(active || null);
    } catch (err) {
      toast.error(err.message || "Erro ao deletar template");
    } finally {
      setLoadingParameters(false);
    }
  };

  // Duplicar template existente (ativo ou não) para um novo rascunho editável,
  // sem tocar no original — abre o formulário de criação pré-preenchido.
  const handleDuplicateTemplate = (template) => {
    setIsCreatingTemplate(true);
    setNewTemplateName(`${template.template_name} (cópia)`);
    setNewParameters(JSON.parse(JSON.stringify(template.parameters || [])));
  };

  const handleToggleTransferLinkProduct = (productId) => {
    setTransferLinkedProductIds((prev) =>
      prev.includes(productId) ? prev.filter((pid) => pid !== productId) : [...prev, productId]
    );
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setProductData((prevData) => ({
      ...prevData,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Basic validation
      if (!productData.name || !productData.productCode) {
        throw new Error(
          "Nome do produto e Código do Produto são obrigatórios."
        );
      }

      const productToUpdate = {
        product_code: productData.productCode,
        name: productData.name,
        description: productData.description,
        is_cured: productData.isCured,
        curing_time_days: productData.isCured
          ? parseInt(productData.curingTimeDays)
          : null,
        initial_weight_kg: Number(productData.initialWeightKg),
        cured_weight_kg: productData.isCured
          ? Number(productData.curedWeightKg)
          : null,
        min_stock_quantity: Number(productData.minStockQuantity),
        max_stock_quantity: Number(productData.maxStockQuantity),
        advanced_details: null, // Campo removido da interface
        image_url: productData.imageLink || null, // Enviando URL da imagem
        box_type_dpd: productData.boxTypeDpd || null, // Tipo de caixa para clientes DPD
        box_type_ox: productData.boxTypeOx || null, // Tipo de caixa para clientes OX
        validade_dias: Number(productData.validadeDias) || 365,
        is_pack: productData.isPack, // Novo campo
        units_per_pack: productData.isPack ? Number(productData.unitsPerPack) : 1, // Novo campo
        is_active: productData.isActive, // Novo campo
        produtividade: Number(productData.produtividade) || 20, // Novo campo
        is_variant: productData.isVariant, // Novo campo
        parent_product_id: productData.isVariant ? productData.parentProductId : null, // Novo campo
        for_all_clients: productData.forAllClients, // Produto para todos os clientes
        sold_by: productData.soldBy || "unit",
        hide_from_orders_when_out_of_stock: productData.hideFromOrdersWhenOutOfStock, // Ocultar das encomendas se stock zerado
      };

      await productService.updateProduct(id, productToUpdate);
      await productService.updateTransferLinks(id, enableStockTransfer ? transferLinkedProductIds : []);
      setSuccess("Produto atualizado com sucesso!");
      toast.success("Produto atualizado com sucesso!");
      navigate("/stock"); // Uncomment to navigate after update
    } catch (err) {
      setError(err.message || "Erro ao atualizar produto.");
      toast.error(err.message || "Erro ao atualizar produto.");
      console.error("Erro ao atualizar produto:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isAdminOrSupervisor) {
      toast.error("Você não tem permissão para excluir produtos.");
      return;
    }

    // Gerar código aleatório de 6 dígitos
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setRandomCode(code);
    setEnteredCode(""); // Limpa o campo de entrada do código

    if (currentUserRole === "supervisor") {
      setShowPasswordModal(true); // Abre o modal para supervisores
    } else if (currentUserRole === "admin") {
      // Admins não precisam de senha, excluem diretamente
      if (
        window.confirm(
          "Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita."
        )
      ) {
        await executeDelete();
      }
    }
  };

  const executeDelete = async (password = null) => {
    setLoading(true);
    try {
      await productService.deleteProduct(id, password); // Passa a senha diretamente
      toast.success("Produto excluído com sucesso!");
      navigate("/stock"); // Navegar para a lista de produtos
    } catch (err) {
      setError(err.message || "Erro ao excluir produto.");
      toast.error(err.message || "Erro ao excluir produto.");
      console.error("Erro ao excluir produto:", err);
    } finally {
      setLoading(false);
      setShowPasswordModal(false); // Fecha o modal após a tentativa
      setDeletePassword(""); // Limpa a senha
    }
  };

  const handleConfirmDeleteWithPassword = () => {
    if (!deletePassword) {
      toast.error("Por favor, insira a senha.");
      return;
    }
    if (
      currentUserRole === "supervisor" &&
      enteredCode !== randomCode
    ) {
      toast.error("Código de confirmação incorreto.");
      return;
    }
    executeDelete(deletePassword);
  };

  // Renderiza a árvore de parâmetros para o painel "Ver campos": secções
  // aparecem como cabeçalho com o próprio conteúdo aninhado logo abaixo, em
  // vez de listar o nó "group" como se fosse um campo comum (mostrando só o
  // tipo cru "group", sem revelar o que tem dentro da secção).
  const renderParameterNodesView = (nodes, depth = 0) => {
    return (nodes || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((node, index) => {
        if (!node) return null;

        if (node.type === "group") {
          return (
            <div
              key={node.id || `group-${depth}-${index}`}
              className="bg-white rounded border border-purple-100 overflow-hidden"
            >
              <div className="flex items-center space-x-2 px-2 py-1 bg-purple-100/60">
                <FontAwesomeIcon icon={faLayerGroup} className="text-purple-500 text-xs" />
                <span className="font-medium text-neutral-800 text-xs">
                  {node.name || "(Secção sem nome)"}
                </span>
                <span className="text-xs text-purple-600">
                  {(node.children || []).length} item(ns)
                </span>
              </div>
              <div className="pl-3 pr-2 py-2 space-y-1 border-l-2 border-purple-200 ml-2">
                {(node.children || []).length > 0 ? (
                  renderParameterNodesView(node.children, depth + 1)
                ) : (
                  <p className="text-xs text-purple-500 italic py-1">
                    Secção vazia — nenhum campo adicionado ainda.
                  </p>
                )}
              </div>
            </div>
          );
        }

        return (
          <div
            key={node.id || `field-${depth}-${index}`}
            className="flex items-center justify-between bg-white p-2 rounded border border-purple-100 text-xs"
          >
            <div className="flex items-center space-x-2">
              <span className="font-medium text-neutral-800">{node.name}</span>
              {node.required && <span className="text-red-500">*</span>}
            </div>
            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
              {getParameterTypeLabel(node.type)}
            </span>
          </div>
        );
      });
  };

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 text-neutral-600 hover:text-neutral-900"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
        </button>
        <h1 className="text-2xl">Editar Produto</h1>
      </div>

      {loading && (
        <div className="text-center text-neutral-500">
          Carregando produto...
        </div>
      )}

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          {error}
        </div>
      )}
      {success && (
        <div
          className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          {success}
        </div>
      )}

      {!loading && !error && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Image Link - Not in DB schema, keeping for UI but won't be sent */}
              <div>
                <label
                  htmlFor="imageLink"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Link da Imagem
                </label>
                <input
                  type="url"
                  id="imageLink"
                  placeholder="https://"
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  value={productData.imageLink}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label
                  htmlFor="name"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Nome do Produto
                </label>
                <input
                  type="text"
                  id="name"
                  value={productData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="productCode"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Código do Produto
                </label>
                <input
                  type="text"
                  id="productCode"
                  value={productData.productCode}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Descrição
                </label>
                <textarea
                  id="description"
                  rows="4"
                  value={productData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                ></textarea>
              </div>


              <BoxTypeSelect
                id="boxTypeDpd"
                label="Tipo de Caixa DPD"
                value={productData.boxTypeDpd}
                onChange={handleBoxTypeDpdChange}
                options={boxTypeOptions}
              />

              <BoxTypeSelect
                id="boxTypeOx"
                label="Tipo de Caixa OX"
                value={productData.boxTypeOx}
                onChange={handleBoxTypeOxChange}
                options={boxTypeOptions}
              />

              <div>
                <label
                  htmlFor="validadeDias"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Validade (dias)
                </label>
                <input
                  type="number"
                  id="validadeDias"
                  value={productData.validadeDias}
                  onChange={handleChange}
                  min="1"
                  max="3650"
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  placeholder="365"
                />
              </div>

              <div>
                <label
                  htmlFor="produtividade"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Produtividade (%)
                </label>
                <input
                  type="number"
                  id="produtividade"
                  value={productData.produtividade}
                  onChange={handleChange}
                  min="1"
                  max="100"
                  step="0.01"
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  placeholder="20"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Exemplo: 20% = 20kg de queijo para 100L de leite
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="initialWeightKg"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Peso por unidade (kg)
                </label>
                <input
                  type="number"
                  id="initialWeightKg"
                  value={productData.initialWeightKg}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  step="0.01"
                />
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isCured"
                  checked={productData.isCured}
                  onChange={handleChange}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="isCured"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Queijo com Cura
                </label>
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isPack"
                  checked={productData.isPack}
                  onChange={handleChange}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="isPack"
                  className="ml-2 text-sm text-neutral-700"
                >
                  É um Pack
                </label>
              </div>

              {productData.isPack && (
                <div className="mb-6">
                  <label
                    htmlFor="unitsPerPack"
                    className="block text-sm text-neutral-700 mb-2"
                  >
                    Quantas unidades tem no pack?
                  </label>
                  <input
                    type="number"
                    id="unitsPerPack"
                    value={productData.unitsPerPack}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                    required
                  />
                </div>
              )}

              <div className="mb-6">
                <label
                  htmlFor="soldBy"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Vendido por
                </label>
                <select
                  id="soldBy"
                  value={productData.soldBy}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                >
                  <option value="unit">Unidade (dividir por unidade)</option>
                  <option value="kg">Peso em kg (dividir por kg)</option>
                </select>
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={productData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="isActive"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Produto Ativo
                </label>
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="forAllClients"
                  checked={productData.forAllClients}
                  onChange={handleChange}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="forAllClients"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Produto para todos os clientes
                </label>
              </div>
              {!productData.forAllClients && (
                <p className="text-xs text-amber-600 mb-4 -mt-2 ml-6">
                  Este produto precisará ser ativado individualmente para cada cliente na tela de detalhes do cliente.
                </p>
              )}

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="hideFromOrdersWhenOutOfStock"
                  data-testid="product-hide-from-orders-when-out-of-stock"
                  checked={productData.hideFromOrdersWhenOutOfStock}
                  onChange={handleChange}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="hideFromOrdersWhenOutOfStock"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Ocultar das encomendas se stock zerado
                </label>
              </div>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isVariant"
                  checked={productData.isVariant}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="isVariant"
                  className="ml-2 text-sm text-neutral-700"
                >
                  É variante?
                </label>
              </div>

              {productData.isVariant && (
                <div className="mb-6">
                  <label
                    htmlFor="parentProductId"
                    className="block text-sm text-neutral-700 mb-2"
                  >
                    Produto Pai *
                  </label>
                  <select
                    id="parentProductId"
                    value={productData.parentProductId}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                    required={productData.isVariant}
                  >
                    <option value="">Selecione o produto pai</option>
                    {availableParents
                      .filter(p => p.id !== id) // Não mostrar o próprio produto
                      .map(parent => (
                        <option key={parent.id} value={parent.id}>
                          {parent.name} ({parent.product_code})
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">
                    Variantes compartilham stock e lotes com o produto pai
                  </p>
                </div>
              )}

              {productData.isCured && (
                <div id="cureFields" className="space-y-6">
                  <div>
                    <label
                      htmlFor="curedWeightKg"
                      className="block text-sm text-neutral-700 mb-2"
                    >
                      Peso Curado (kg)
                    </label>
                    <input
                      type="number"
                      id="curedWeightKg"
                      value={productData.curedWeightKg}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="curingTimeDays"
                      className="block text-sm text-neutral-700 mb-2"
                    >
                      Tempo de Cura (dias)
                    </label>
                    <input
                      type="number"
                      id="curingTimeDays"
                      value={productData.curingTimeDays}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                    />
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="minStockQuantity"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Stock Mínimo
                </label>
                <input
                  type="number"
                  id="minStockQuantity"
                  value={productData.minStockQuantity}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  step="0.01"
                />
              </div>

              <div>
                <label
                  htmlFor="maxStockQuantity"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Stock Máximo
                </label>
                <input
                  type="number"
                  id="maxStockQuantity"
                  value={productData.maxStockQuantity}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Parâmetros de Produção */}
          <div className="mt-8 pt-6 border-t border-neutral-200">
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-4">
                {/* Checkbox para mostrar Parâmetros de Produção */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showAdvancedDetails"
                    checked={showAdvancedDetails}
                    onChange={(e) => setShowAdvancedDetails(e.target.checked)}
                    className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                  />
                  <label
                    htmlFor="showAdvancedDetails"
                    className="ml-2 text-sm text-neutral-700"
                  >
                    Configurar Parâmetros de Produção
                  </label>
                </div>

                {/* Conteúdo dos Parâmetros de Produção */}
                {showAdvancedDetails && (
                  <div className="border border-neutral-200 rounded-lg p-4 space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-neutral-800">
                          Parâmetros de Produção
                        </h3>
                        {activeTemplate && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            <FontAwesomeIcon icon={faCheck} className="mr-1" />
                            Template Ativo: v{activeTemplate.version_number}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-500 mb-4">
                        Configure campos que devem ser preenchidos na produção deste produto.
                      </p>

                      {loadingParameters ? (
                        <div className="text-center py-4 text-neutral-500">
                          Carregando parâmetros...
                        </div>
                      ) : (
                        <>
                          {/* Formulário de criação de template */}
                          {isCreatingTemplate ? (
                            <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4">
                              <h4 className="font-medium text-blue-800 mb-3">
                                Novo Template de Parâmetros
                              </h4>
                              <div className="mb-4">
                                <label className="block text-sm text-neutral-700 mb-1">
                                  Nome do Template
                                </label>
                                <input
                                  type="text"
                                  value={newTemplateName}
                                  onChange={(e) => setNewTemplateName(e.target.value)}
                                  placeholder="Ex: Parâmetros de Qualidade"
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                                />
                              </div>
                              <div className="mb-4">
                                <label className="block text-sm text-neutral-700 mb-2">
                                  Campos do Template
                                </label>
                                <ParameterTemplateEditor
                                  parameters={newParameters}
                                  onChange={setNewParameters}
                                />
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={handleCreateTemplate}
                                  disabled={loadingParameters}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
                                >
                                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                                  Salvar Template
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsCreatingTemplate(false);
                                    setNewTemplateName("");
                                    setNewParameters([]);
                                  }}
                                  className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 text-sm"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setIsCreatingTemplate(true);
                                setNewTemplateName(`Versão ${parameterTemplates.length + 1}`);
                              }}
                              className="mb-4 px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 text-sm"
                            >
                              <FontAwesomeIcon icon={faPlus} className="mr-2" />
                              Criar Novo Template
                            </button>
                          )}

                          {/* Lista de templates existentes */}
                          {parameterTemplates.length > 0 && (
                            <div className="border border-neutral-200 rounded-lg overflow-hidden">
                              <div className="bg-neutral-50 px-4 py-2 border-b border-neutral-200">
                                <span className="text-sm font-medium text-neutral-600">
                                  Templates ({parameterTemplates.length})
                                </span>
                              </div>
                              <div className="divide-y divide-neutral-200">
                                {parameterTemplates.map((template) => (
                                  <div
                                    key={template.id}
                                    className={`p-3 ${template.is_active ? "bg-green-50" : ""}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <span className="font-medium text-neutral-800">
                                          {template.template_name}
                                        </span>
                                        <span className="text-xs text-neutral-500 ml-2">
                                          (v{template.version_number})
                                        </span>
                                        {template.is_active && (
                                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                                            Ativo
                                          </span>
                                        )}
                                        <div className="text-xs text-neutral-500 mt-1">
                                          {flattenFields(template.parameters).length} campos
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        {/* Botão de visualizar campos */}
                                        <button
                                          type="button"
                                          onClick={() => setViewingFields(viewingFields === template.id ? null : template.id)}
                                          className={`px-2 py-1 text-xs rounded ${
                                            viewingFields === template.id
                                              ? "bg-purple-100 text-purple-700"
                                              : "text-purple-600 hover:bg-purple-50"
                                          }`}
                                          title="Visualizar campos do template"
                                        >
                                          <FontAwesomeIcon icon={faEye} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDuplicateTemplate(template)}
                                          className="px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-100 rounded"
                                          title="Duplicar este template para um novo rascunho editável"
                                        >
                                          <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                        {!template.is_active ? (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() => handleActivateTemplate(template.id)}
                                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                            >
                                              Ativar
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleDeleteTemplate(template.id)}
                                              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                                            >
                                              <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                          </>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => handleDeactivateTemplate(template.id)}
                                            className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                                          >
                                            <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                            Desativar
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Painel de visualização dos campos */}
                                    {viewingFields === template.id && (
                                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                        <h5 className="text-xs font-medium text-purple-800 mb-2">
                                          Campos do Template
                                        </h5>
                                        {template.parameters && template.parameters.length > 0 ? (
                                          <div className="space-y-1">
                                            {renderParameterNodesView(template.parameters)}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-purple-600 italic">
                                            Nenhum campo configurado
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {parameterTemplates.length === 0 && !isCreatingTemplate && (
                            <p className="text-sm text-neutral-500 italic">
                              Nenhum template de parâmetros configurado.
                            </p>
                          )}
                        </>
                      )}
                    </div>

                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Transferência de Stock entre Produtos */}
          <div className="mt-8 pt-6 border-t border-neutral-200">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="enableStockTransfer"
                checked={enableStockTransfer}
                onChange={(e) => setEnableStockTransfer(e.target.checked)}
                className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
              />
              <label
                htmlFor="enableStockTransfer"
                className="ml-2 text-sm text-neutral-700"
              >
                Habilitar transferência de stock com outros produtos
              </label>
            </div>

            {enableStockTransfer && (
              <div className="border border-neutral-200 rounded-lg p-4">
                <p className="text-sm text-neutral-500 mb-3">
                  Selecione com quais produtos {productData.name || "este produto"} pode enviar/receber peso.
                </p>
                {allProductsForTransfer.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {allProductsForTransfer.map((product) => (
                      <label key={product.id} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={transferLinkedProductIds.includes(product.id)}
                          onChange={() => handleToggleTransferLinkProduct(product.id)}
                          className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                        />
                        <span>{product.name} ({product.product_code})</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 italic">Nenhum outro produto disponível.</p>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons Responsivos */}
          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-4">
            {/* Botão de exclusão */}
            <div className="flex justify-center sm:justify-start">
              {isAdminOrSupervisor && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  {loading ? "Excluindo..." : "Excluir Produto"}
                </button>
              )}
            </div>
            
            {/* Botões principais */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full sm:w-auto px-6 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
                disabled={loading}
              >
                {loading ? "Guardando..." : "Guardar Alterações"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Modal de Confirmação de Senha para Exclusão */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirmar Exclusão</h3>
            <p className="mb-4">
              Por favor, insira a senha de supervisor para confirmar a exclusão
              deste produto.
            </p>
            {currentUserRole === "supervisor" && (
              <div className="mb-4">
                <p className="text-sm text-neutral-700">
                  Digite o código de confirmação:{" "}
                  <strong className="text-red-600">{randomCode}</strong>
                </p>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg mt-2"
                  placeholder="Digite o código"
                  value={enteredCode}
                  onChange={(e) => setEnteredCode(e.target.value)}
                />
              </div>
            )}
            <input
              type="password"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-4"
              placeholder="Senha de Supervisor"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowPasswordModal(false)}
                className="px-6 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteWithPassword}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProducts;
